import { useState, useEffect, useMemo } from 'react'
import { Button } from '@/components/Button'
import { SelectField } from '@/components/SelectField'
import { SightMapEmbed } from '@/components/SightMapEmbed'
import { fetchFloors, type AssetTarget } from '@/api/fetchCollection'
import { getLabels } from '@/data/config'
import type { ApiUnit, ApiFloor } from '@/types/api'

interface MapPlaceholderProps {
	/** Property whose floors to load; defaults to the active config's asset. */
	asset?: AssetTarget
	/** Filtered unit IDs to highlight. Omit to count every available unit in the floor picker. */
	sightmapUnitIds?: (string | number)[]
	locateUnitId?: string | number
	/** Floor of the located unit. locateUnitById leaves the embed on the overview, so the picker can't learn it from floor.change. */
	locateFloorId?: string | number | null
	onUnitClick?: (unitId: string) => void
	mockUnits?: ApiUnit[]
	/** Alternate embeddable map URL supplied via the `view.change.before` hook. Forwarded to SightMapEmbed. */
	mapUrlOverride?: string | null
	/** Draws the panel's own card frame. Turn off when a parent card wraps the map. */
	framed?: boolean
	/**
	 * Keep SightMap's building-image floor picker. Only the desktop full map
	 * view opts in; every other map hides it.
	 */
	showFloorPicker?: boolean
	className?: string
}

// Shared by every full-height map panel (split view, map view, detail pages) so
// the map fills the viewport consistently. Pairs with `spaces:sticky spaces:top-16`.
export const MAP_HEIGHT_CLASS = 'spaces:h-[calc(100vh-2rem)]'

const MOCK_FLOORS: ApiFloor[] = [
	{ id: '1', name: 'Floor 1', filter_label: '1', filter_short_label: '1', level: 1, sort: 1, unit_ids: [] },
	{ id: '2', name: 'Floor 2', filter_label: '2', filter_short_label: '2', level: 2, sort: 2, unit_ids: [] },
	{ id: '3', name: 'Floor 3', filter_label: '3', filter_short_label: '3', level: 3, sort: 3, unit_ids: [] },
	{ id: '4', name: 'Floor 4', filter_label: '4', filter_short_label: '4', level: 4, sort: 4, unit_ids: [] }
]

// Mock units all share one SightMap floor, so spread them across the mock floors for the counts.
function buildMockFloors (mockUnits: ApiUnit[]): ApiFloor[] {
	return MOCK_FLOORS.map((floor, i) => ({
		...floor,
		unit_ids: mockUnits.filter((_, j) => j % MOCK_FLOORS.length === i).map(u => String(u.id))
	}))
}

export function MapPlaceholder ({ asset, sightmapUnitIds, locateUnitId, locateFloorId, onUnitClick, mockUnits, mapUrlOverride, framed = true, showFloorPicker = false, className = '' }: MapPlaceholderProps) {
	const isMock = mockUnits !== undefined
	const [floors, setFloors] = useState<ApiFloor[]>([])
	const [selectedFloorId, setSelectedFloorId] = useState('')

	useEffect(() => {
		if (isMock) {
			setFloors(buildMockFloors(mockUnits))
			return
		}

		let cancelled = false
		fetchFloors(asset)
			.then(result => { if (!cancelled) setFloors(result) })
			.catch(() => { if (!cancelled) setFloors([]) })
		return () => { cancelled = true }
	// Callers key the map per property, so the asset is fixed for this mount.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isMock])

	// The located unit's floor, falling back to the floor whose units include it
	// (mock units carry a SightMap floor ID that matches no mock floor).
	const locatedFloorId = useMemo(() => {
		if (locateUnitId === undefined) return null
		const byId = floors.find(f => f.id === String(locateFloorId ?? ''))
		const byUnit = floors.find(f => f.unit_ids.includes(String(locateUnitId)))
		return (byId ?? byUnit)?.id ?? null
	}, [floors, locateUnitId, locateFloorId])

	// Follow the located unit. Keyed on the floor alone so a floor the user picks afterwards sticks.
	useEffect(() => {
		if (locatedFloorId) setSelectedFloorId(locatedFloorId)
	}, [locatedFloorId])

	// Default to the first floor (endpoint returns them sorted) once floors load.
	useEffect(() => {
		if (locateUnitId !== undefined) return
		if (floors.length > 0 && selectedFloorId === '') {
			setSelectedFloorId(floors[0].id)
		}
	}, [floors, selectedFloorId, locateUnitId])

	// Hide the floor dropdown entirely for assets with fewer than two floors.
	const showFloorSelect = floors.length >= 2
	// Each option shows how many of the floor's units match the current filters, or
	// every available unit when no filtered set is passed (e.g. Unit Details).
	// Floors with no matches stay selectable so the user can still view them on the map.
	// The count carries the CMS Units tab label so it doesn't read as another floor number.
	const { unitTabLabel } = getLabels()
	const floorOptions = useMemo(() => {
		const matching = sightmapUnitIds ? new Set(sightmapUnitIds.map(String)) : null
		return floors.map(f => {
			const count = matching ? f.unit_ids.filter(id => matching.has(id)).length : f.unit_ids.length
			return {
				value: f.id,
				label: f.name,
				secondaryLabel: `${count} ${unitTabLabel}`
			}
		})
	}, [floors, sightmapUnitIds, unitTabLabel])

	return (
		<div
			className={`spaces-map spaces:relative spaces:bg-canvas-base spaces:overflow-hidden spaces:min-h-[400px]${framed ? ' spaces:rounded-24 spaces:border spaces:border-canvas-3' : ''} ${className}`}
		>
			<SightMapEmbed sightmapUnitIds={sightmapUnitIds} locateUnitId={locateUnitId} onUnitClick={onUnitClick} mockUnits={mockUnits} mapUrlOverride={mapUrlOverride} selectedFloorId={selectedFloorId || undefined} onFloorChange={setSelectedFloorId} framed={framed} showFloorPicker={showFloorPicker} className='spaces:w-full spaces:h-full' />

			{showFloorSelect && (
				<div className='spaces-map-controls spaces:absolute spaces:top-16 spaces:left-16 spaces:z-[1]'>
					<MapViewControls options={floorOptions} value={selectedFloorId} onValueChange={setSelectedFloorId} />
				</div>
			)}
		</div>
	)
}

interface MapZoomControlsProps {
	onZoomIn?: () => void
	onZoomOut?: () => void
	onReset?: () => void
	className?: string
}

export function MapZoomControls ({ onZoomIn, onZoomOut, onReset, className = '' }: MapZoomControlsProps) {
	return (
		<div
			className={`spaces-map-zoom-controls spaces:flex spaces:flex-col spaces:items-center spaces:p-4 spaces:bg-canvas-base spaces:border spaces:border-canvas-3 spaces:rounded-12 spaces:shadow-xs-below ${className}`}
		>
			<Button
				iconOnly='Plus'
				appearance='ghost'
				color='content4'
				size='sm'
				aria-label='Zoom in'
				onClick={onZoomIn}
			/>
			<Button
				iconOnly='Minus'
				appearance='ghost'
				color='content4'
				size='sm'
				aria-label='Zoom out'
				onClick={onZoomOut}
			/>
			<Button
				iconOnly='Maximize'
				appearance='ghost'
				color='content4'
				size='sm'
				aria-label='Reset zoom'
				onClick={onReset}
			/>
		</div>
	)
}

interface MapViewControlsProps {
	options: Array<{ value: string; label: string; secondaryLabel?: string }>
	value: string
	onValueChange: (value: string) => void
	className?: string
}

/**
 * Floor picker overlay for the map. Replaces the native SightMap floor
 * selection UI. The 2D/3D view toggle shown in the design is out of scope
 * for this phase.
 */
export function MapViewControls ({
	options,
	value,
	onValueChange,
	className = ''
}: MapViewControlsProps) {
	return (
		<div
			className={`spaces-map-view-controls spaces:inline-flex spaces:items-center spaces:gap-4 spaces:bg-canvas-base spaces:border spaces:border-canvas-3 spaces:rounded-16 spaces:p-4 spaces:shadow-sm-below ${className}`}
		>
			<SelectField
				options={options}
				value={value}
				onValueChange={onValueChange}
				label='Floor:'
				showCaret
			/>
		</div>
	)
}
